<template>
    <div class="card">
        <h5 class="mb-3">Manage Company Logo</h5>

        <div class="mb-3">
            <b>The uploaded logo will be shown inside AMS portal in header and footer</b>
        </div>

        <div class="flex flex-column gap-3">

            <!-- Upload -->
            <label class="p-button p-button-outlined cursor-pointer flex align-items-center gap-2 w-fit">
                <i class="pi pi-upload"></i>
                Choose Logo
                <input ref="logoInput" type="file" class="hidden" accept=".jpg,.jpeg,.png,.svg"
                    @change="onLogoSelected" />
            </label>

            <!-- Preview -->
            <div v-if="logoPreview" class="flex align-items-center gap-3">
                <img :src="logoPreview" class="logo-preview" />

                <Button icon="pi pi-times" class="p-button-rounded p-button-danger" @click="removeLogo" />
            </div>

            <!-- Actions -->
            <div class="flex gap-2">
                <Button label="Save" icon="pi pi-check" :loading="loading" @click="saveLogo" />
                <Button label="Cancel" class="p-button-secondary" @click="goBack" />
            </div>

        </div>
    </div>
</template>

<script setup>

import { ref, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { showToast } from '@/helpers/toast-handler';
import authApi from '@/api/axios-api';
import GlobalRequest from '@/api/global-request';

const route = useRoute();
const router = useRouter();
const companyId = route.params.id;
const logoPreview = ref(''); // Base64 or S3 preview URL
const logoInput = ref(null);
const loading = ref(false);

const uploadedLogoId = ref(null);

onMounted(() => {
    loadCompanyLogo();
});

async function loadCompanyLogo() {
    try {
        const res = await GlobalRequest.getRequest(`/v2/admin/companies/${companyId}`);

        logoPreview.value = res.data.logo?.vignette
            ? res.data.logo.vignette
            : res.data.logo?.thumbnail;

    } catch (e) {
        console.error(e);
    }
}

async function onLogoSelected(event) {
    const file = event.target.files[0];

    if (!file) {
        showToast({}, 'No file selected!');
        return;
    }

    if (!file.type.startsWith('image/')) {
        showToast({}, 'Only image files allowed!');
        resetInput();
        return;
    }

    if (file.size > 1024 * 1024) {
        showToast({}, 'Max size is 1MB');
        resetInput();
        return;
    }

    // preview
    const reader = new FileReader();
    reader.onload = (e) => {
        logoPreview.value = e.target.result;
    };
    reader.readAsDataURL(file);

    const formData = new FormData();
    formData.append('file', file);
    formData.append('company_id', companyId);

    loading.value = true;

    try {
        const res = await authApi.post('/v2/upload', formData, {
            headers: {
                'Content-Type': 'multipart/form-data'
            }
        });


        if (res.status === 201) {
            uploadedLogoId.value = res.data.data?.id;
            logoPreview.value = res.data.s3_uri ?? logoPreview.value;

            showToast({ severity: 'success', summary: 'Success' }, 'Uploaded! Please click save to save changes.');

        } else {
            showToast({}, 'Upload failed!');
        }

    } catch (e) {
        console.error(e);
        showToast({}, 'Upload failed!');
    } finally {
        loading.value = false;
        resetInput();
    }
}

async function saveLogo() {
    if (!uploadedLogoId.value) {
        showToast({}, 'Please upload the logo');
        return;
    }

    loading.value = true;

    try {
        await GlobalRequest.patchRequest(
            `/v2/admin/companies/${companyId}/logo`,
            { logo_id: uploadedLogoId.value }
        );

        showToast({everity: 'success', summary: 'Success'}, 'Logo saved successfully');
        router.back();

    } catch (e) {
        console.error(e);
        showToast({}, 'Failed to save logo');
    } finally {
        loading.value = false;
    }
}

async function removeLogo() {
    loading.value = true;

    try {
        await GlobalRequest.deleteRequest(
            `/v2/admin/companies/${companyId}/logo`
        );

        uploadedLogoId.value = null;
        logoPreview.value = '';

        showToast({everity: 'success', summary: 'Success'}, 'Logo removed successfully');

    } catch (e) {
        console.error(e);
        showToast({}, 'Failed to remove logo');
    } finally {
        loading.value = false;
        resetInput();
    }
}

function resetInput() {
    if (logoInput.value) {
        logoInput.value.value = '';
    }
}

function goBack() {
    router.back();
}
</script>

<style scoped>
.logo-preview {
    width: 100px;
    height: 100px;
    border-radius: 8px;
    object-fit: cover;
}
</style>
